'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2RepoFileRequest, PrV2RepoFileResult } from '@/lib/pr-v2/code-changes/types';
import type { QueryClient } from '@tanstack/react-query';
import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';
import { fetchPrV2RepoFile } from './fetch-pr-v2';
import { prV2RepoFileQueryKey } from './query-keys';
import { MINUTE } from '@/lib/constants';
type PrV2RepoFileComparison = {
  headSha: string | null;
  baseSha: string | null;
};
function prV2RepoFileQueryOptions(owner: string, repo: string, number: string, request: PrV2RepoFileRequest, {
  headSha,
  baseSha
}: PrV2RepoFileComparison) {
  return queryOptions({
    queryKey: prV2RepoFileQueryKey(owner, repo, number, request, headSha, baseSha),
    queryFn: headSha && baseSha ? ({
      signal
    }) => fetchPrV2RepoFile(owner, repo, number, {
      ...request,
      headSha,
      baseSha
    }, signal) : skipToken,
    // The comparison SHAs in the key pin the revision, so a file read once never goes stale.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * MINUTE,
    // The app-wide default carries the previous key's data into a new key, which would show the
    // last file under the next file's name until it loads.
    placeholderData: () => undefined,
    refetchOnWindowFocus: false
  });
}

/**
 * Reads one repository file at the comparison the diff shows. It waits until both comparison SHAs
 * are known, because the route reads the commit they name.
 */
export function usePrV2RepoFile(owner, repo, number, request, comparison) {
  const $ = _c(6);
  let t0;
  if ($[0] !== comparison || $[1] !== number || $[2] !== owner || $[3] !== repo || $[4] !== request) {
    t0 = prV2RepoFileQueryOptions(owner, repo, number, request, comparison);
    $[0] = comparison;
    $[1] = number;
    $[2] = owner;
    $[3] = repo;
    $[4] = request;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  return useQuery(t0);
}

/**
 * Imperative counterpart to usePrV2RepoFile for click-time consumers. It shares the hook's query,
 * so the panel shows a file a click already read without fetching it again. Rejects when the
 * comparison SHAs are unknown, since nothing pins the revision to read.
 */
export function ensurePrV2RepoFile(queryClient: QueryClient, owner: string, repo: string, number: string, request: PrV2RepoFileRequest, comparison: PrV2RepoFileComparison): Promise<PrV2RepoFileResult> {
  if (!comparison.headSha || !comparison.baseSha) {
    return Promise.reject(new Error('ensurePrV2RepoFile requires headSha and baseSha'));
  }
  return queryClient.fetchQuery(prV2RepoFileQueryOptions(owner, repo, number, request, comparison));
}